import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { Footer } from '../components/Footer';
import { BookingModal } from '../components/BookingModal';
import { TvIcon, WifiIcon, MonitorPlayIcon, ShowerHeadIcon, PhoneIcon, ClockIcon, CoffeeIcon, ParkingCircleIcon } from 'lucide-react';
const rooms = [{
  type: 'Einzelzimmer',
  price: '49,95 €',
  desc: 'Gemütliches Zimmer für Alleinreisende mit allem Komfort.'
}, {
  type: 'Doppelzimmer',
  price: '69,95 €',
  desc: 'Geräumiges Zimmer für zwei Personen mit Doppelbett.'
}, {
  type: '4-Bett Zimmer',
  price: '99,95 €',
  desc: 'Ideal für Familien oder Gruppen mit bis zu vier Schlafplätzen.'
}];
const amenities = [{
  icon: TvIcon,
  label: 'Flat-TV'
}, {
  icon: WifiIcon,
  label: 'Kostenloses WLAN'
}, {
  icon: MonitorPlayIcon,
  label: 'SKY'
}, {
  icon: ShowerHeadIcon,
  label: 'Dusche / Bad'
}, {
  icon: CoffeeIcon,
  label: 'Frühstück verfügbar'
}, {
  icon: ParkingCircleIcon,
  label: 'Kostenlose Parkplätze'
}];
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function HotelPage() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  return <>
      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} data-id="element-420" />
      <PageHeader title="Hotel" subtitle="60 Zimmer. 3 Sterne. Ihr Zuhause an der A9." image="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=1200&q=80" accentColor="text-transparent bg-clip-text bg-gradient-to-r from-white to-electric-blue" accentGlow="bg-electric-blue" data-id="element-421" />

      <section className="py-20 px-4 bg-base" data-id="element-422">
        <div className="max-w-7xl mx-auto" data-id="element-423">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mb-20" data-id="element-424">
            <h2 className="text-4xl font-heading font-bold mb-6 text-white" data-id="element-425">
              Erholung auf höchstem Niveau
            </h2>
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-426">
              Unser 3-Sterne-Hotel bietet 60 moderne und komfortable Zimmer für
              Geschäftsreisende, Familien und Durchreisende. Direkt an der A9
              gelegen, verbinden wir bequeme Erreichbarkeit mit ruhigem
              Schlafkomfort. Buchen Sie direkt und unkompliziert.
            </p>
          </motion.div>

          <h3 className="text-3xl font-heading font-bold mb-8 text-white" data-id="element-427">
            Unsere Zimmer
          </h3>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-428">
            {rooms.map(room => <motion.div key={room.type} variants={fadeUp} className="glass-card p-8 rounded-2xl border-electric-blue/10 flex flex-col" data-id="element-429">
                <h4 className="text-xl font-heading font-bold mb-2 text-white" data-id="element-430">
                  {room.type}
                </h4>
                <p className="text-white/60 mb-6 flex-1" data-id="element-431">{room.desc}</p>
                <div className="text-3xl font-heading font-bold text-electric-blue" data-id="element-432">
                  ab {room.price}
                </div>
                <p className="text-white/40 text-sm mt-1" data-id="element-433">pro Nacht</p>
              </motion.div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="glass-card p-8 rounded-2xl border-electric-blue/10 mb-12" data-id="element-434">
            <div className="flex items-center gap-3 mb-4" data-id="element-435">
              <CoffeeIcon className="w-5 h-5 text-electric-blue" data-id="element-436" />
              <h3 className="text-xl font-heading font-bold text-white" data-id="element-437">
                Frühstück
              </h3>
            </div>
            <p className="text-white/80 mb-1" data-id="element-438">
              Reichhaltiges Frühstücksbuffet:{' '}
              <span className="text-white font-bold" data-id="element-439">9,95 €</span> pro Person
            </p>
            <p className="text-white/60 text-sm" data-id="element-440">
              Montag – Sonntag: 06:00 – 11:00 Uhr
            </p>
          </motion.div>

          <h3 className="text-3xl font-heading font-bold mb-8 text-white" data-id="element-441">
            Ausstattung
          </h3>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-20" data-id="element-442">
            {amenities.map(a => {
            const Icon = a.icon;
            return <motion.div key={a.label} variants={fadeUp} className="glass-card p-5 rounded-xl flex items-center gap-4" data-id="element-443">
                  <Icon className="w-5 h-5 text-electric-blue shrink-0" strokeWidth={1.5} data-id="element-444" />
                  <span className="text-white/80" data-id="element-445">{a.label}</span>
                </motion.div>;
          })}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="glass-card p-8 rounded-2xl border-electric-blue/30 max-w-xl relative overflow-hidden" data-id="element-446">
            <div className="absolute top-0 right-0 w-32 h-32 bg-electric-blue/10 blur-3xl rounded-full pointer-events-none" data-id="element-447" />
            <h3 className="text-2xl font-heading font-bold text-white mb-4" data-id="element-448">
              Jetzt buchen
            </h3>
            <p className="text-white/60 mb-8" data-id="element-449">
              Prüfen Sie unsere Verfügbarkeiten und reservieren Sie Ihr Zimmer
              direkt online.
            </p>
            <button onClick={() => setIsBookingModalOpen(true)} className="w-full sm:w-auto px-8 py-4 rounded-xl bg-electric-blue text-white font-bold text-lg hover:bg-electric-blue/90 transition-colors mb-6" data-id="element-450">
              Online buchen
            </button>
            <div className="h-px w-full bg-white/10 mb-6" data-id="element-451" />
            <div className="flex items-center gap-3" data-id="element-452">
              <span className="text-white/40 text-sm" data-id="element-453">Oder telefonisch:</span>
              <PhoneIcon className="w-4 h-4 text-electric-blue" data-id="element-454" />
              <a href="tel:+499273501730" className="text-white hover:text-electric-blue transition-colors font-medium" data-id="element-455">
                09273 / 50173-0
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-456" />
    </>;
}